'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import JSZip from 'jszip';

type OutputFormat = 'source' | 'png' | 'jpeg' | 'webp';

type BatchPreset = {
  id: string;
  label: string;
  bestFor: string;
  output: OutputFormat;
  quality: number;
  maxWidth: number;
  hint: string;
};

type BatchFile = {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
  sourceUrl: string;
  outputUrl: string | null;
  outputName: string | null;
  outputSize: number | null;
  outputLabel: string | null;
  error: string | null;
};

const presets: BatchPreset[] = [
  {
    id: 'wordpress',
    label: 'WordPress',
    bestFor: 'Blog and CMS uploads',
    output: 'source',
    quality: 0.8,
    maxWidth: 2200,
    hint: 'A balanced preset for WordPress uploads where images should stay clean without feeling oversized inside the CMS.',
  },
  {
    id: 'shopify',
    label: 'Shopify',
    bestFor: 'Storefront images',
    output: 'jpeg',
    quality: 0.78,
    maxWidth: 2048,
    hint: 'Useful for storefront and product-supporting images that still need to feel polished on modern shop themes.',
  },
  {
    id: 'blog-hero',
    label: 'Blog hero',
    bestFor: 'Wide editorial headers',
    output: 'webp',
    quality: 0.8,
    maxWidth: 2400,
    hint: 'A good fit for large article headers and wide editorial visuals where lighter delivery matters.',
  },
  {
    id: 'article-images',
    label: 'Article inline',
    bestFor: 'Inline article visuals',
    output: 'jpeg',
    quality: 0.76,
    maxWidth: 1600,
    hint: 'Useful when inline article images should stay lighter and easier to publish in batches.',
  },
  {
    id: 'modern-web',
    label: 'Modern web',
    bestFor: 'Modern browser delivery',
    output: 'webp',
    quality: 0.8,
    maxWidth: 2000,
    hint: 'Best when you want smaller modern image files for web publishing.',
  },
  {
    id: 'listings',
    label: 'Listings',
    bestFor: 'Forms and marketplaces',
    output: 'jpeg',
    quality: 0.74,
    maxWidth: 1600,
    hint: 'Useful for directories, forms, marketplaces, and any upload flow that prefers lighter images.',
  },
  {
    id: 'profile-images',
    label: 'Profile images',
    bestFor: 'Avatars and portraits',
    output: 'jpeg',
    quality: 0.84,
    maxWidth: 1200,
    hint: 'Best for profile photos, avatars, and portrait-style images that need to stay crisp without being heavy.',
  },
  {
    id: 'screenshots-graphics',
    label: 'Screenshots',
    bestFor: 'UI captures and graphics',
    output: 'png',
    quality: 0.92,
    maxWidth: 1800,
    hint: 'Good for screenshots, simple graphics, and cleaner edges where PNG still makes sense.',
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string | null;
  extension: string | null;
  qualityEnabled: boolean;
}> = [
  {
    value: 'source',
    label: 'Keep best format',
    mimeType: null,
    extension: null,
    qualityEnabled: true,
  },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
];

const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Image';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getPreferredOutput(sourceType: string, selectedOutput: OutputFormat) {
  if (selectedOutput !== 'source') {
    return outputOptions.find((option) => option.value === selectedOutput) ?? outputOptions[1];
  }

  if (sourceType === 'image/jpeg') {
    return outputOptions.find((option) => option.value === 'jpeg') ?? outputOptions[1];
  }

  if (sourceType === 'image/webp') {
    return outputOptions.find((option) => option.value === 'webp') ?? outputOptions[2];
  }

  return outputOptions.find((option) => option.value === 'png') ?? outputOptions[3];
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function calculateTargetSize(width: number, height: number, maxWidth: number) {
  if (width <= maxWidth) {
    return { width, height };
  }

  const ratio = maxWidth / width;

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function getSavingsPercent(sourceSize: number, resultSize: number) {
  if (!sourceSize || resultSize >= sourceSize) {
    return 0;
  }

  return Math.round(((sourceSize - resultSize) / sourceSize) * 100);
}

export default function BrowserBatchImageOptimizer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<BatchFile[]>([]);
  const [activePresetId, setActivePresetId] = useState(presets[0].id);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(presets[0].output);
  const [quality, setQuality] = useState(presets[0].quality);
  const [maxWidth, setMaxWidth] = useState<number>(presets[0].maxWidth);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isPreparingZip, setIsPreparingZip] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(null);

  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activePreset = presets.find((preset) => preset.id === activePresetId) ?? null;
  const optimizedCount = files.filter((file) => file.outputUrl).length;
  const totalSourceSize = files.reduce((sum, file) => sum + file.size, 0);
  const totalResultSize = files.reduce((sum, file) => sum + (file.outputSize ?? 0), 0);
  const totalSavings =
    totalResultSize > 0 ? getSavingsPercent(totalSourceSize, totalResultSize) : 0;
  const selectedPreviewFile =
    files.find((file) => file.id === selectedPreviewId) ??
    files.find((file) => file.outputUrl) ??
    files[0] ??
    null;

  useEffect(() => {
    return () => {
      files.forEach((file) => {
        URL.revokeObjectURL(file.sourceUrl);
        if (file.outputUrl) {
          URL.revokeObjectURL(file.outputUrl);
        }
      });
    };
  }, [files]);

  function resetOutputs() {
    setFiles((currentFiles) =>
      currentFiles.map((file) => {
        if (file.outputUrl) {
          URL.revokeObjectURL(file.outputUrl);
        }

        return {
          ...file,
          outputUrl: null,
          outputName: null,
          outputSize: null,
          outputLabel: null,
          error: null,
        };
      }),
    );
  }

  function applyPreset(preset: BatchPreset) {
    setActivePresetId(preset.id);
    setOutputFormat(preset.output);
    setQuality(preset.quality);
    setMaxWidth(preset.maxWidth);
    resetOutputs();
  }

  async function handleIncomingFiles(fileList: FileList | File[] | null) {
    setErrorMessage(null);
    setCreditMessage(null);

    if (!fileList?.length) {
      return;
    }

    const nextFiles = Array.from(fileList);
    const validFiles = nextFiles
      .slice(0, 10)
      .filter((file) => supportedTypes.has(file.type) && file.size <= 5 * 1024 * 1024);

    if (!validFiles.length) {
      setErrorMessage(
        'Choose up to 10 PNG, JPG, JPEG, WebP, GIF, or BMP files, with each file up to 5 MB.',
      );
      return;
    }

    if (validFiles.length < nextFiles.length) {
      setErrorMessage(
        'Some files were skipped because the tool supports up to 10 browser-friendly images and 5 MB per file.',
      );
    }

    const prepared = await Promise.all(
      validFiles.map(async (file) => {
        const image = await loadImageFromFile(file);
        return {
          id: crypto.randomUUID(),
          file,
          name: file.name,
          type: file.type,
          size: file.size,
          width: image.naturalWidth,
          height: image.naturalHeight,
          sourceUrl: URL.createObjectURL(file),
          outputUrl: null,
          outputName: null,
          outputSize: null,
          outputLabel: null,
          error: null,
        } satisfies BatchFile;
      }),
    );

    setFiles((currentFiles) => {
      currentFiles.forEach((file) => {
        URL.revokeObjectURL(file.sourceUrl);
        if (file.outputUrl) {
          URL.revokeObjectURL(file.outputUrl);
        }
      });

      return prepared;
    });
    setSelectedPreviewId(prepared[0]?.id ?? null);
  }

  async function runOptimization() {
    if (!files.length) {
      setErrorMessage('Choose up to 10 images first.');
      return;
    }

    setIsOptimizing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'image-optimization', units: files.length }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another optimization run right now.';
        throw new Error(message);
      }

      const processedFiles = await Promise.all(
        files.map(async (file) => {
          const image = await loadImageFromFile(file.file);
          const nextSize = calculateTargetSize(file.width, file.height, maxWidth);
          const output = getPreferredOutput(file.type, outputFormat);
          const canvas = document.createElement('canvas');
          canvas.width = nextSize.width;
          canvas.height = nextSize.height;
          const context = canvas.getContext('2d');

          if (!context) {
            return {
              ...file,
              error: 'This browser could not prepare the image.',
            };
          }

          context.drawImage(image, 0, 0, nextSize.width, nextSize.height);

          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(
              (nextBlob) => resolve(nextBlob),
              output.mimeType ?? 'image/png',
              output.qualityEnabled ? quality : undefined,
            );
          });

          if (!blob) {
            return {
              ...file,
              error: 'This file could not be optimized in the browser.',
            };
          }

          if (file.outputUrl) {
            URL.revokeObjectURL(file.outputUrl);
          }

          return {
            ...file,
            outputUrl: URL.createObjectURL(blob),
            outputName: `${getBaseName(file.name)}-optimized.${output.extension ?? 'png'}`,
            outputSize: blob.size,
            outputLabel: output.label,
            error: null,
          };
        }),
      );

      setFiles(processedFiles);
      setSelectedPreviewId((current) => current ?? processedFiles[0]?.id ?? null);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The batch could not be optimized.');
    } finally {
      setIsOptimizing(false);
    }
  }

  function downloadAll() {
    files.forEach((file, index) => {
      if (!file.outputUrl || !file.outputName) {
        return;
      }

      window.setTimeout(() => {
        const anchor = document.createElement('a');
        anchor.href = file.outputUrl!;
        anchor.download = file.outputName!;
        anchor.click();
      }, index * 140);
    });
  }

  async function downloadZip() {
    const readyFiles = files.filter((file) => file.outputUrl && file.outputName);

    if (!readyFiles.length) {
      return;
    }

    setIsPreparingZip(true);

    try {
      const zip = new JSZip();

      await Promise.all(
        readyFiles.map(async (file) => {
          const response = await fetch(file.outputUrl!);
          const blob = await response.blob();
          zip.file(file.outputName!, blob);
        }),
      );

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });
      const zipUrl = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = zipUrl;
      anchor.download = 'cleanor-optimized-images.zip';
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(zipUrl), 4000);
    } finally {
      setIsPreparingZip(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Optimize up to 10 images in one pass</h2>
          <p>
            Prepare a small batch for websites, CMS uploads, and lighter publishing without sending
            files to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Popular optimization presets">
        {presets.map((preset) => (
          <button
            key={preset.id}
            className={`tool-live-route ${activePresetId === preset.id ? 'tool-live-route-active' : ''}`}
            onClick={() => applyPreset(preset)}
            type="button"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {activePreset ? (
        <div className="tool-batch-preset-note">
          <strong>{activePreset.label}</strong>
          <span>{activePreset.bestFor}</span>
          <span>{`${activePreset.maxWidth}px max width`}</span>
          <span>
            {activePreset.output === 'source'
              ? 'Keeps the best source-friendly format'
              : `Exports as ${outputOptions.find((option) => option.value === activePreset.output)?.label ?? 'image'}`}
          </span>
        </div>
      ) : null}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleIncomingFiles(event.dataTransfer.files);
            }}
            type="button"
          >
            <div>
              <strong>
                {files.length ? `${files.length} files ready` : 'Choose images or drop them here'}
              </strong>
              <span>
                {files.length
                  ? `${formatFileSize(totalSourceSize)} total • up to 10 files`
                  : 'Up to 10 PNG, JPG, JPEG, WebP, GIF, or BMP files • 5 MB each'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
            className="tool-live-file-input"
            multiple
            onChange={(event) => void handleIncomingFiles(event.target.files)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>Batch size</span>
              <input
                disabled
                readOnly
                type="text"
                value={files.length ? `${files.length} selected` : 'Add up to 10 files'}
              />
            </label>
            <label className="field">
              <span>Export as</span>
              <select
                onChange={(event) => {
                  setActivePresetId('custom');
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputs();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-quality-shell tool-editor-sliders">
            <label className="field tool-live-quality">
              <span>Max width</span>
              <input
                max="3200"
                min="800"
                onChange={(event) => {
                  setActivePresetId('custom');
                  setMaxWidth(Math.max(800, Number(event.target.value) || 800));
                  resetOutputs();
                }}
                step="20"
                type="range"
                value={maxWidth}
              />
              <small>{`${maxWidth}px on the long side`}</small>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.95"
                min="0.45"
                onChange={(event) => {
                  setActivePresetId('custom');
                  setQuality(Number(event.target.value));
                  resetOutputs();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `${Math.round(quality * 100)}% quality`
                  : 'PNG keeps a cleaner export without a quality slider'}
              </small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!files.length || isOptimizing}
              onClick={() => void runOptimization()}
              type="button"
            >
              {isOptimizing
                ? 'Optimizing…'
                : `Optimize ${files.length || ''}${files.length ? ' images' : ''}`.trim()}
            </button>
            {optimizedCount ? (
              <>
                <button className="button button-ghost" onClick={downloadAll} type="button">
                  Download all
                </button>
                <button
                  className="button button-ghost"
                  disabled={isPreparingZip}
                  onClick={() => void downloadZip()}
                  type="button"
                >
                  {isPreparingZip ? 'Preparing ZIP…' : 'Download ZIP'}
                </button>
              </>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}

          {files.length ? (
            <div className="tool-batch-list">
              {files.map((file) => (
                <article className="tool-batch-item" key={file.id}>
                  <div className="tool-batch-item-top">
                    <strong>{file.name}</strong>
                    <span>{formatFileSize(file.size)}</span>
                  </div>
                  <div className="tool-batch-item-meta">
                    <span>{normalizeSourceLabel(file.type)}</span>
                    <span>{`${file.width} × ${file.height}`}</span>
                    {file.outputLabel && file.outputSize ? (
                      <span>{`${file.outputLabel} • ${formatFileSize(file.outputSize)}`}</span>
                    ) : null}
                  </div>
                  <div className="tool-batch-item-actions">
                    <button
                      className={`tool-batch-link ${selectedPreviewFile?.id === file.id ? 'tool-batch-link-active' : ''}`}
                      onClick={() => setSelectedPreviewId(file.id)}
                      type="button"
                    >
                      Preview
                    </button>
                    {file.outputUrl && file.outputName ? (
                      <a className="soft-link" download={file.outputName} href={file.outputUrl}>
                        Download
                      </a>
                    ) : (
                      <span className="tool-batch-muted">
                        {file.error ? file.error : 'Ready for optimization'}
                      </span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Ready for web publishing</h3>
          <div className="tool-mockup-chip-list">
            <span>Up to 10 files</span>
            <span>5 MB each</span>
            <span>Client-side</span>
          </div>
          <p>
            {activePreset?.hint ??
              'Use a preset, review the savings, and download a lighter batch that is easier to publish.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to a lighter web-friendly format or
              keep the best source-compatible format.
            </span>
          </div>
          <div className="tool-live-stats">
            <div>
              <strong>Batch</strong>
              <span>{files.length ? `${files.length} files` : 'Up to 10 files'}</span>
            </div>
            <div>
              <strong>Source total</strong>
              <span>{files.length ? formatFileSize(totalSourceSize) : '—'}</span>
            </div>
            <div>
              <strong>Result total</strong>
              <span>{optimizedCount ? formatFileSize(totalResultSize) : '—'}</span>
            </div>
            <div>
              <strong>Savings</strong>
              <span>{optimizedCount ? `${totalSavings}% smaller` : '—'}</span>
            </div>
            <div>
              <strong>Credits</strong>
              <span>{files.length ? `${files.length} for this batch` : '1 per image'}</span>
            </div>
            <div>
              <strong>Download</strong>
              <span>{optimizedCount ? 'Single files or one ZIP' : 'Ready after optimization'}</span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>
        </aside>
      </div>

      {selectedPreviewFile ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Selected file preview</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame">
              <img alt="Original preview" src={selectedPreviewFile.sourceUrl} />
            </div>
            <div className="tool-live-preview-frame">
              {selectedPreviewFile.outputUrl ? (
                <img alt="Optimized preview" src={selectedPreviewFile.outputUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Optimized preview</strong>
                  <span>Run the optimizer to see the result for this file.</span>
                </div>
              )}
            </div>
          </div>
          <p>
            {selectedPreviewFile.name}
            {selectedPreviewFile.outputSize
              ? ` • ${formatFileSize(selectedPreviewFile.outputSize)}`
              : ''}
            {selectedPreviewFile.outputSize
              ? ` • ${getSavingsPercent(selectedPreviewFile.size, selectedPreviewFile.outputSize)}% smaller`
              : ''}
          </p>
        </div>
      ) : null}
    </article>
  );
}
